'use client';

import { trackEvent } from '@svs/nova-core/analytics';
import { getFormattedDateAndTime } from '@svs/nova-poolbetting/core/lib';
import type { DrawData } from '@svs/nova-poolbetting/schema/draw';
import { Button } from '@svs/ui/react/button';
import type { ReactNode } from 'react';

type DrawCardChangeDrawButtonProps = {
  drawData: DrawData;
  isSelected: boolean;
  selectedText?: string;
  unselectedText?: string;
};

function composeExtraMoneyString(extraMoney: DrawData['fund']): string {
  if (extraMoney.jackpot) {
    return `jackpot:${extraMoney.jackpot}`;
  }
  if (extraMoney.guaranteeJackpot) {
    return `garanti-jackpot:${extraMoney.guaranteeJackpot}`;
  }
  if (extraMoney.extraMoney) {
    return `extra-pengar:${extraMoney.extraMoney}`;
  }
  if (extraMoney.rolloverIn) {
    return `rullpott:${extraMoney.rolloverIn}`;
  }

  return '';
}

const composeDrawTitleForAnalytics = (draw: DrawData): string => {
  const productName = 'Powerplay'; // Hardcoded since this component is only used in Powerplay, can be changed to dynamic if reused in other products in the future
  const { drawNumber, regCloseTime } = draw.draw;
  const { fund } = draw;

  const drawTitle = getFormattedDateAndTime(regCloseTime, { dateFormatOptions: 'dayAsText' });
  const extraMoneySummary = composeExtraMoneyString(fund);
  const productAndDrawString = `${productName}_${drawTitle}(#${drawNumber})`;

  return extraMoneySummary ? `${productAndDrawString}_${extraMoneySummary}` : productAndDrawString;
};

/**
 * Component for change draw button in draw card.
 *
 * @param root0 props
 * @param root0.drawData data for current draw
 * @param root0.isSelected boolean for if current draw is selected
 * @param root0.selectedText text to display when draw is selected
 * @param root0.unselectedText text to display when draw is not selected
 * @returns Button to change draw, disabled if draw is selected
 */
export default function DrawCardChangeDrawButton({
  drawData,
  isSelected,
  selectedText,
  unselectedText,
}: DrawCardChangeDrawButtonProps): ReactNode {
  const handleButtonClick = (): void => {
    trackEvent({ category: 'Nya tipsen', action: 'change_draw', optLabel: composeDrawTitleForAnalytics(drawData) });
  };
  return isSelected ? (
    <Button className="w-full min-w-full" variant="outline" disabled>
      {selectedText || 'Vald'}
    </Button>
  ) : (
    <Button onClick={handleButtonClick} className="w-full min-w-full" variant="outline" disabled={false}>
      {unselectedText || 'Välj'}
    </Button>
  );
}
